/**
 * Create Document Template - Wizard-based template creation/editing
 * Uses DocumentTemplateCreateWizard for clean multi-step flow
 * Supports both create and edit modes
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DocumentTemplateCreateWizard } from '../../components/club-documents/v2';
import { AdminPageLayout } from '../../components';
import apiService from '../../services/api';
import { clubDocumentApi } from '../../services/clubDocumentApi';
import { useToast } from '../../contexts/ToastContext';

const CreateDocumentTemplate = () => {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const { showError } = useToast();
  const isEditMode = Boolean(templateId);
  const [club_id, setClubId] = useState(null);
  const [detectedScope, setDetectedScope] = useState(null);
  const [loading, setLoading] = useState(true);
  const [templateData, setTemplateData] = useState(null);

  useEffect(() => {
    loadContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  const loadContext = async () => {
    try {
      setLoading(true);
      const userInfo = await apiService.getCurrentUser();
      const isPlatformAdmin = userInfo.is_platform_admin || userInfo.primary_role === 'platform_admin';
      const userClubId = userInfo.club_id;

      // Auto-detect scope: platform admin with no club → platform scope, club admin → club scope
      let scope;
      let clubIdValue;
      if (isPlatformAdmin && !userClubId) {
        scope = 'platform';
        clubIdValue = 'platform';
        setDetectedScope('platform');
        setClubId('platform');
      } else if (userClubId) {
        scope = 'club';
        clubIdValue = userClubId;
        setDetectedScope('club');
        setClubId(userClubId);
      }

      // If in edit mode, load the template data
      if (isEditMode && templateId) {
        console.log('[CreateDocumentTemplate] Loading template:', templateId);
        const result = await clubDocumentApi.getTemplate(templateId);
        console.log('[CreateDocumentTemplate] API result:', result);
        if (result.success && result.data) {
          console.log('[CreateDocumentTemplate] Template loaded successfully:', result.data);
          setTemplateData(result.data);
        } else {
          console.error('[CreateDocumentTemplate] Failed to load template:', result);
          showError('Failed to load template data');
          const redirectPath = scope === 'platform'
            ? '/admin/platform/document-templates'
            : '/admin/club/document-templates';
          navigate(redirectPath);
          return;
        }
      }
    } catch (err) {
      console.error('Error loading context:', err);
      showError('Failed to load template');
      // Redirect to club templates as fallback
      navigate('/admin/club/document-templates');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    const redirectPath = detectedScope === 'platform'
      ? '/admin/platform/document-templates'
      : '/admin/club/document-templates';
    navigate(redirectPath);
  };

  const handleSuccess = (response) => {
    console.log(`Template ${isEditMode ? 'updated' : 'created'} successfully:`, response);
    const redirectPath = detectedScope === 'platform'
      ? '/admin/platform/document-templates'
      : '/admin/club/document-templates';
    navigate(redirectPath);
  };

  if (loading) {
    return (
      <AdminPageLayout
        title={`${isEditMode ? 'EDIT' : 'CREATE'} DOCUMENT TEMPLATE`}
        subtitle="Loading wizard..."
        maxWidth="1400px"
      >
        <div style={{ padding: '4rem', textAlign: 'center', opacity: 0.6 }}>
          Loading template wizard...
        </div>
      </AdminPageLayout>
    );
  }

  // In edit mode, ensure we have template data before rendering wizard
  if (isEditMode && !templateData) {
    console.log('[CreateDocumentTemplate] Edit mode but no template data, showing error');
    return (
      <AdminPageLayout
        title="EDIT DOCUMENT TEMPLATE"
        subtitle="Loading failed"
        maxWidth="1400px"
      >
        <div style={{ padding: '4rem', textAlign: 'center' }}>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            Failed to load template data. Redirecting...
          </p>
        </div>
      </AdminPageLayout>
    );
  }

  console.log('[CreateDocumentTemplate] Rendering wizard with:', {
    club_id,
    templateId,
    isEditMode,
    hasTemplateData: !!templateData
  });

  return (
    <DocumentTemplateCreateWizard
      club_id={club_id}
      templateId={templateId}
      initialData={templateData}
      isEditMode={isEditMode}
      onClose={handleClose}
      onSuccess={handleSuccess}
    />
  );
};

export default CreateDocumentTemplate;
